import { get, isEmpty, isEqual, isFunction, map, size } from 'lodash';
import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';
import { TrackByValue, XuxGridActionPayload } from '@app/shared/types';
import { IDropdownMenuOption } from '@app/shared/types/ui';
import { trackByValue } from '@app/shared/utils/common-utils';
import { UiActions } from '@app/store/actions/ui/ui.actions';
import { DropdownMenuService } from '@geniuspos/ui-shared/modules/xdm-shared-service/dropdown-menu/dropdown-menu.service';

import { AbstractBaseRenderer } from '../abstract-base-renderer/abstract-base-renderer';
import { getValueForPinnedCell } from '../renderer-helpers';

@Component({
  selector: 'xvi-array-string-renderer',
  templateUrl: './xvi-array-string-renderer.component.html',
  styleUrls: ['./xvi-array-string-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XviArrayStringRendererComponent
  extends AbstractBaseRenderer
  implements AgRendererComponent, OnDestroy
{
  public stringValue: string = '';
  public placeholder: string;
  public items: IDropdownMenuOption[] = [];
  public valueMapper: Function = null;
  public translateMapper: Function = null;
  public itemClass: string = '';
  public wrapperClass: string = '';
  public allButton: string = '';
  public linkClick: (event: XuxGridActionPayload) => Promise<void>;
  public dropdownMenuClick: (event: {
    option: IDropdownMenuOption;
  }) => Promise<void>;
  public trackByValue: TrackByValue = trackByValue;

  public constructor(
    public i18nService: XVII18nService,
    public uiActions: UiActions,
    private dropDownMenuService: DropdownMenuService,
    private changeDetector: ChangeDetectorRef,
  ) {
    super();
  }

  public agInit(params: ICellRendererParams): void {
    super.agInit(params);
    this.params = params;
    this.placeholder = get(params, 'placeholder', this.placeholder);
    this.valueMapper = get(params, 'valueMapper', this.valueMapper);
    this.translateMapper = get(params, 'translateMapper', this.translateMapper);
    this.wrapperClass = get(params, 'wrapperClass', this.wrapperClass);
    this.itemClass = get(params, 'itemClass', this.itemClass);
    this.captionFieldPath = get(
      params,
      'captionFieldPath',
      this.captionFieldPath,
    );
    const linkClick = get(params, 'linkClick');

    if (isFunction(linkClick)) {
      // Use the wrapper to pass in the row data and other info
      this.linkClick = (event) => {
        return linkClick({
          ...event,
          field: this.params.colDef.field,
          rowData: [this.params.node.data],
          rowId: this.params.node.id,
          rowIndex: this.params.node.rowIndex,
          value: this.items,
        });
      };
    }

    this.dropdownMenuClick = get(params, 'dropdownMenuClick');
    this.refresh(params);
  }

  // eslint-disable-next-line
  public refresh(params: ICellRendererParams): boolean {
    // This should be done for every renderer
    // Either we digest new value in this component or
    // need to tell the grid to replace this component by returning "false"
    // Then grid will destroy this component and crete a new one, obviously running agInit.
    if (this.getItemsToShow(params) === this.stringValue) {
      return true;
    }

    this.refreshRendererState(params);

    if (get(params, 'node.rowPinned')) {
      if (this.gridApi) {
        this.stringValue = getValueForPinnedCell(params, this.gridApi);
      }
      this.changeDetector.markForCheck();

      return true;
    }

    this.stringValue = this.getItemsToShow(params);
    this.setCaptionForQuickFilterString(this.stringValue);
    this.allButton = this.getAllButton();
    this.changeDetector.markForCheck();

    return true;
  }

  public getItemsToShow(params: ICellRendererParams): string {
    const placeholderValue = this.i18nService.t(this.placeholder) ?? '';

    const formattedItems = map(this.handleValue(params.value), 'title').join(
      ', ',
    );

    return !isEmpty(this.handleValue(params.value))
      ? formattedItems
      : placeholderValue;
  }

  public getAllButton(): string {
    const itemsCount = this.items.length;
    if (itemsCount === 0) return '';

    return this.i18nService.translate(
      isEqual(size(this.items), 1)
        ? I18n.Common.CountItem
        : I18n.Common.CountItems,
      { count: this.items.length },
    );
  }

  private refreshRendererState(params): void {
    this.items = this.handleValue(get(params, 'value', []));
  }

  private handleValue(value): IDropdownMenuOption[] {
    const translatedValue = this.translateMapper
      ? this.translateMapper(value)
      : value;

    return isFunction(this.valueMapper)
      ? this.valueMapper(translatedValue, this)
      : translatedValue;
  }

  public ngOnDestroy(): void {
    this.dropDownMenuService.clearContainer();
  }

  public handleDropDownMenuClick(event: { option: IDropdownMenuOption }): void {
    if (isFunction(this.dropdownMenuClick)) {
      this.dropdownMenuClick(event);
    }
  }
}
